/**
 * Mappers between the backend `annotations` row shape and the
 * react-pdf-highlighter `IHighlight` shape used throughout the client.
 *
 * Backend (`models.Annotation`):
 *   annotation_id       — server-side primary key, only useful for PATCH/DELETE
 *   annotation_client_id — the IHighlight.id from when the annotation was created
 *   category_name       — IHighlight.comment.category
 *   text_content        — IHighlight.content.text
 *   image_url           — IHighlight.content.image
 *   comment_text        — IHighlight.comment.text
 *   annotation_data     — JSON-encoded IHighlight.position
 *
 * Client (`IHighlight`):
 *   id, content {text?, image?}, comment {text, category}, position
 *
 * The mapping is lossy on the way to the row (loses things like
 * `usePdfCoordinates` if added to position later — preserved through
 * the JSON blob). On the way back, the position is parsed and dropped
 * fields default to empty strings rather than undefined to keep the
 * highlighter happy.
 */

import type { IHighlight } from "@argument-studio/react-pdf-highlighter-with-categories";
import type { Annotation } from "@/types/models";

/**
 * Build the body for POST /annotations from a freshly-created IHighlight.
 * IHighlight.id (a client-side uuid) becomes annotation_client_id, which
 * is what the unique constraint keys on.
 */
export function highlightToCreateRequest(h: IHighlight) {
  return {
    annotation_client_id: h.id,
    category_name: h.comment.category || null,
    text_content: h.content.text ?? null,
    image_url: h.content.image ?? null,
    comment_text: h.comment.text || null,
    annotation_data: JSON.stringify(h.position),
  };
}

/** Convert a backend annotations row into the IHighlight shape. */
export function annotationToHighlight(a: Annotation): IHighlight | null {
  if (!a.annotation_data) return null;
  let position: IHighlight["position"];
  try {
    position = JSON.parse(a.annotation_data) as IHighlight["position"];
  } catch (err) {
    console.error("annotationToHighlight: position JSON parse failed:", err);
    return null;
  }
  // The highlighter's groupHighlightsByPage iterates position.rects, so a
  // stored position missing it (legacy or area/image annotations) crashes
  // the whole reader. Drop structurally-invalid positions and guarantee
  // rects is an array — area highlights legitimately carry an empty rects
  // and render from boundingRect.
  if (!position || typeof position !== "object" || !position.boundingRect) {
    console.warn(
      "annotationToHighlight: dropping annotation with invalid position",
      a.annotation_id
    );
    return null;
  }
  if (!Array.isArray(position.rects)) {
    position.rects = [];
  }
  // Recover a usable top-level pageNumber. Rows can lack it (legacy blob
  // shapes kept it per-rect; the pre-fix update thunk truncated positions
  // to bare boundingRects) or carry the fork's -1 "unknown page" sentinel.
  // Without a valid page the sidebar sort has nothing to group by and
  // interleaves pages. Prefer the first valid (>= 1) candidate.
  const validPage = (n: unknown): n is number =>
    typeof n === "number" && Number.isFinite(n) && n >= 1;
  if (!validPage(position.pageNumber)) {
    const candidate = [
      position.boundingRect.pageNumber,
      position.rects[0]?.pageNumber,
    ].find(validPage);
    position.pageNumber = candidate ?? 1;
  }
  return {
    id: a.annotation_client_id ?? a.annotation_id,
    position,
    content: {
      text: a.text_content ?? undefined,
      image: a.image_url ?? undefined,
    },
    comment: {
      text: a.comment_text ?? "",
      category: a.category_name ?? "",
    },
  };
}

/** Map an array of annotations to highlights, dropping ones that failed to parse. */
export function annotationsToHighlights(annotations: Annotation[]): IHighlight[] {
  return annotations
    .map(annotationToHighlight)
    .filter((h): h is IHighlight => h !== null);
}
